import type { MatchRecord } from "@/lib/api/rsvp";

interface PlayerStats {
  id: number;
  name: string;
  wins: number;
  played: number;
  winStreak: number;
}

function playerWon(match: MatchRecord, playerId: number): boolean {
  const inA = match.teama?.includes(playerId);
  return (inA && match.winner === "A") || (!inA && match.winner === "B");
}

function computePlayerStats(
  players: { id: number; name: string }[],
  matches: MatchRecord[]
): PlayerStats[] {
  const sorted = [...matches].sort(
    (a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
  );

  return players.map((player) => {
    const played = sorted.filter(
      (m) => m.teama?.includes(player.id) || m.teamb?.includes(player.id)
    );
    const wins = played.filter((m) => playerWon(m, player.id)).length;

    let streak = 0;
    for (let i = played.length - 1; i >= 0; i--) {
      if (playerWon(played[i], player.id)) streak++;
      else break;
    }

    return { id: player.id, name: player.name, wins, played: played.length, winStreak: streak };
  });
}

function rankByWins(stats: PlayerStats[]): Map<number, number> {
  const sorted = [...stats].sort((a, b) => b.wins - a.wins || b.played - a.played);
  const ranks = new Map<number, number>();
  sorted.forEach((s, i) => ranks.set(s.id, i + 1));
  return ranks;
}

/** Compute post-match highlight lines for the completed card teaser. */
export function computePostMatchHighlights(
  players: { id: number; name: string }[],
  allMatches: MatchRecord[],
  latestMatch: MatchRecord
): string[] {
  if (!latestMatch.scorea && latestMatch.scorea !== 0) return [];

  const before = allMatches.filter((m) => m.id !== latestMatch.id);
  const after = allMatches;

  const beforeStats = computePlayerStats(players, before);
  const afterStats = computePlayerStats(players, after);
  const beforeRanks = rankByWins(beforeStats);
  const afterRanks = rankByWins(afterStats);

  const lines: string[] = [];

  for (const stat of afterStats) {
    const prevRank = beforeRanks.get(stat.id);
    const newRank = afterRanks.get(stat.id);
    if (prevRank != null && newRank != null && newRank < prevRank) {
      lines.push(`📈 ${stat.name} subió al puesto #${newRank} del Ranking General`);
      break;
    }
  }

  for (const stat of afterStats) {
    if (stat.winStreak >= 2) {
      lines.push(`🔥 ${stat.name} extendió su racha a ${stat.winStreak} victorias consecutivas`);
      break;
    }
  }

  if (lines.length === 0 && latestMatch.winner === "Draw") {
    lines.push("🤝 Empate clásico — la tabla sigue apretada.");
  }

  return lines.slice(0, 3);
}
